import { useRef, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  escapeCitationsForMarkdown,
  buildMarkdownComponents,
} from "./CitationButton";

/* ─────────────── healMarkdown ─────────────── */

/**
 * "Heal" incomplete markdown so ReactMarkdown can parse it mid-stream.
 *
 * Only the trailing edge of the text can be incomplete. This function
 * closes any dangling syntax so the parser doesn't choke:
 *   - Unclosed fenced code blocks (```)
 *   - Unclosed bold (**), italic (*), inline code (`)
 */
export const healMarkdown = (text) => {
  let s = text;

  // 1. Unclosed fenced code block — close it
  const fenceOpen = (s.match(/```/g) || []).length;
  if (fenceOpen % 2 !== 0) {
    s += "\n```";
  }

  // 2. Unclosed inline code (odd number of backticks outside fences)
  const withoutFences = s.replace(/```[\s\S]*?```/g, "");
  const backticks = (withoutFences.match(/`/g) || []).length;
  if (backticks % 2 !== 0) {
    s += "`";
  }

  // 3. Unclosed bold ** — check last line for dangling **
  const lastLine = s.split("\n").pop() || "";
  const boldMarkers = (lastLine.match(/\*\*/g) || []).length;
  if (boldMarkers % 2 !== 0) {
    s += "**";
  }

  // 4. Unclosed italic * (single, not part of **) — check last line
  const singleStars = lastLine.replace(/\*\*/g, "").match(/\*/g) || [];
  if (singleStars.length % 2 !== 0) {
    s += "*";
  }

  return s;
};

/* ─────────────── StreamingMessage ─────────────── */

/**
 * Streaming renderer — uses ReactMarkdown with healed text so formatting
 * (bold, italic, headings, lists, code) renders live during streaming.
 * The healMarkdown preprocessor closes any dangling syntax at the tail
 * so the parser always receives valid markdown.
 */
const StreamingMessage = ({ text, citations }) => {
  const hoveredRef = useRef(null);

  const citationMap = useMemo(() => {
    if (!citations || citations.length === 0) return {};
    const map = {};
    citations.forEach((c) => {
      map[c.index] = c;
    });
    return map;
  }, [citations]);

  const onHoverIn = useCallback((idx) => {
    hoveredRef.current = idx;
  }, []);
  const onHoverOut = useCallback(() => {
    hoveredRef.current = null;
  }, []);

  const components = useMemo(
    () =>
      buildMarkdownComponents(citationMap, hoveredRef, onHoverIn, onHoverOut),
    [citationMap, onHoverIn, onHoverOut],
  );

  const escaped = useMemo(() => {
    if (!text) return "";
    let s =
      citations && citations.length > 0
        ? escapeCitationsForMarkdown(text)
        : text;
    return healMarkdown(s);
  }, [text, citations]);

  if (!text) return null;

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {escaped}
    </ReactMarkdown>
  );
};

export default StreamingMessage;
